import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { HotelsShopSelectors } from "@b2bportal/core-hotels";
import {
  type HotelShoppedProductGroupedTrackingProperties,
  type EventTransformerHooks,
  useEventTracking,
  type UseInstantTransformEvent,
  type HotelShoppedProductTrackingProperties,
  logNotFoundTrackingProperties,
  fallbackHotelShoppedProductTrackingProperties,
} from "@b2bportal/core-tracking";

import {
  CheckoutEventName,
  type CheckoutRawEvent,
  CheckoutRawEventName,
  type CheckoutContactInformationEnabledRawEvent,
  type CheckoutTravelersEnabledRawEvent,
  type CheckoutFulfillPurchaseButtonEnabledRawEvent,
  type CheckoutPaymentSectionEnabledRawEvent,
  fallbackCheckoutQuotedProductTrackingProperties,
  fallbackCheckoutPaymentMethodTrackingProperties,
  fallbackPurchaseCompletedTrackingProperties,
  type CheckoutQuoteErrorDisplayedRawEvent,
} from "../types";
import {
  HOTEL_PRODUCT_TYPE,
  type HotelCartQuoteErrorViewed,
  type HotelCheckoutBuySuccessfullyCompleted,
  type HotelCheckoutBuyUnsuccessfullyCompleted,
  HotelCheckoutEventName,
  type HotelCheckoutHTSEvent,
  type HotelContactInfoEnabled,
  type HotelPaymentSectionReviewed,
  type HotelPurchaseReviewed,
  type HotelTravelersEnabled,
} from "./events";
import {
  type HotelsCheckoutRawEvent,
  HotelsCheckoutRawEventName,
  type HotelCheckoutPurchaseCompletedRawEvent,
  type HotelsCheckoutTrackingAttributes,
  type HotelCheckoutPurchaseFailedRawEvent,
} from "./types";

export const useHotelCheckoutPurchaseCompletedTransformer: UseInstantTransformEvent<
  HotelCheckoutPurchaseCompletedRawEvent,
  HotelCheckoutBuySuccessfullyCompleted
> = () => {
  const providers = useProviders();
  const roomsCount = useSelector(HotelsShopSelectors.getRoomsCount);

  const purchaseCompletedTrackingProperties =
    getPurchaseCompletedTrackingProperties(providers);
  const encryptedTrackingProperties =
    purchaseCompletedTrackingProperties.encryptedProperties;
  const encryptedPropertiesArray = encryptedTrackingProperties
    ? [encryptedTrackingProperties]
    : [];
  return useCallback(
    (event) => {
      const htsEvent =
        transformEventIfProductIsAHotel<HotelCheckoutBuySuccessfullyCompleted>(
          HotelCheckoutEventName.hotel_complete_buy,
          providers,
          {
            ...getQuoteProperties(providers),
            ...getPaymentMethodProperties(providers),
            booking_id: event.properties.booking_id,
            room_count: roomsCount ?? 1,
            success: true,
          },
          encryptedPropertiesArray
        );

      return htsEvent;
    },
    [providers, roomsCount]
  );
};

export const useHotelCheckoutPurchaseFailedTransformer: UseInstantTransformEvent<
  HotelCheckoutPurchaseFailedRawEvent,
  HotelCheckoutBuyUnsuccessfullyCompleted
> = () => {
  const providers = useProviders();
  const roomsCount = useSelector(HotelsShopSelectors.getRoomsCount);

  return useCallback(
    (_event) => {
      const htsEvent =
        transformEventIfProductIsAHotel<HotelCheckoutBuyUnsuccessfullyCompleted>(
          HotelCheckoutEventName.hotel_complete_buy,
          providers,
          {
            ...getQuoteProperties(providers),
            ...getPaymentMethodProperties(providers),
            ...getCartFulfillFailureReason(providers),
            room_count: roomsCount ?? 1,
            success: false,
          }
        );

      return htsEvent;
    },
    [providers, roomsCount]
  );
};

export const useCheckoutContactInformationEnabledTransformer: UseInstantTransformEvent<
  CheckoutContactInformationEnabledRawEvent,
  HotelContactInfoEnabled
> = () => {
  const providers = useProviders();
  const roomsCount = useSelector(HotelsShopSelectors.getRoomsCount);

  return useCallback(() => {
    const htsEvent = transformEventIfProductIsAHotel<HotelContactInfoEnabled>(
      CheckoutEventName.viewed_contact_info,
      providers,
      { room_count: roomsCount ?? 1 }
    );

    return htsEvent;
  }, [providers, roomsCount]);
};

export const useCheckoutTravelersEnabledTransformer: UseInstantTransformEvent<
  CheckoutTravelersEnabledRawEvent,
  HotelTravelersEnabled
> = () => {
  const providers = useProviders();
  const roomsCount = useSelector(HotelsShopSelectors.getRoomsCount);

  return useCallback(() => {
    const htsEvent = transformEventIfProductIsAHotel<HotelTravelersEnabled>(
      HotelCheckoutEventName.hotel_view_travelers,
      providers,
      { room_count: roomsCount ?? 1 }
    );

    return htsEvent;
  }, [providers, roomsCount]);
};

export const useCheckoutFulfillPurchaseButtonEnabledTransformer: UseInstantTransformEvent<
  CheckoutFulfillPurchaseButtonEnabledRawEvent,
  HotelPurchaseReviewed
> = () => {
  const providers = useProviders();
  const roomsCount = useSelector(HotelsShopSelectors.getRoomsCount);

  return useCallback(() => {
    const htsEvent = transformEventIfProductIsAHotel<HotelPurchaseReviewed>(
      HotelCheckoutEventName.hotel_review_details,
      providers,
      {
        ...getQuoteProperties(providers),
        room_count: roomsCount ?? 1,
      }
    );

    return htsEvent;
  }, [providers, roomsCount]);
};

export const useCheckoutPaymentSectionEnabledTransformer: UseInstantTransformEvent<
  CheckoutPaymentSectionEnabledRawEvent,
  HotelPaymentSectionReviewed
> = () => {
  const providers = useProviders();
  const roomsCount = useSelector(HotelsShopSelectors.getRoomsCount);

  return useCallback(() => {
    const htsEvent =
      transformEventIfProductIsAHotel<HotelPaymentSectionReviewed>(
        CheckoutEventName.view_payment,
        providers,
        {
          ...getQuoteProperties(providers),
          room_count: roomsCount ?? 1,
        },
        [...getQuoteEncryptedProperties(providers)]
      );

    return htsEvent;
  }, [providers, roomsCount]);
};

export const useCheckoutCartQuoteErrorDisplayed: UseInstantTransformEvent<
  CheckoutQuoteErrorDisplayedRawEvent,
  HotelCartQuoteErrorViewed
> = () => {
  const providers = useProviders();

  return useCallback(
    (event) => {
      const htsEvent =
        transformEventIfProductIsAHotel<HotelCartQuoteErrorViewed>(
          CheckoutEventName.modal_alert,
          providers,
          {
            product_type: HOTEL_PRODUCT_TYPE,
            modal_type: event.properties.error.type,
            screen: "hotels_checkout",
            step: "quote",
          }
        );

      return htsEvent;
    },
    [providers]
  );
};

export const HOTEL_CHECKOUT_HTS_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  HotelsCheckoutRawEvent | CheckoutRawEvent,
  HotelCheckoutHTSEvent
> = {
  [HotelsCheckoutRawEventName.HotelCheckoutPurchaseCompleted]: [
    useHotelCheckoutPurchaseCompletedTransformer,
  ],
  [HotelsCheckoutRawEventName.HotelCheckoutPurchaseFailed]: [
    useHotelCheckoutPurchaseFailedTransformer,
  ],
  [CheckoutRawEventName.CheckoutContactInformationEnabled]: [
    useCheckoutContactInformationEnabledTransformer,
  ],
  [CheckoutRawEventName.CheckoutTravelersEnabled]: [
    useCheckoutTravelersEnabledTransformer,
  ],
  [CheckoutRawEventName.CheckoutFulfillPurchaseButtonEnabled]: [
    useCheckoutFulfillPurchaseButtonEnabledTransformer,
  ],
  [CheckoutRawEventName.CheckoutPaymentSectionEnabled]: [
    useCheckoutPaymentSectionEnabledTransformer,
  ],
  [CheckoutRawEventName.CheckoutQuoteErrorDisplayed]: [
    useCheckoutCartQuoteErrorDisplayed,
  ],
};

function useProviders(): HotelsCheckoutProviders {
  const eventTracking = useEventTracking();

  const providers = useMemo(() => {
    return {
      trackingAttributes: {
        getAllAttributes: () =>
          eventTracking.getAllAttributes() as HotelsCheckoutTrackingAttributes,
      },
    };
  }, [eventTracking.getAllAttributes]);

  return providers;
}

export interface HotelsCheckoutProviders {
  trackingAttributes: {
    getAllAttributes: () => HotelsCheckoutTrackingAttributes;
  };
}

const transformEventIfProductIsAHotel = <T extends HotelCheckoutHTSEvent>(
  eventName: T["name"],
  providers: HotelsCheckoutProviders,
  extraProperties: Omit<
    T["properties"],
    keyof HotelShoppedProductGroupedTrackingProperties
  >,
  extraEncryptedProperties: string[] = []
):
  | {
      name: T["name"];
      properties: T["properties"];
      encryptedProperties: T["encryptedProperties"];
    }
  | undefined => {
  const isHotelCheckout =
    providers.trackingAttributes.getAllAttributes()
      .hotelCheckoutShoppedProduct != null;
  if (!isHotelCheckout) {
    return;
  }
  const shoppedProductProperties = getShoppedProductProperties(providers);

  return {
    name: eventName,
    properties: {
      ...shoppedProductProperties.search.properties,
      ...shoppedProductProperties.hotel.properties,
      ...shoppedProductProperties.roomRate.properties,
      ...shoppedProductProperties.room.properties,
      ...extraProperties,
    },
    encryptedProperties: [
      shoppedProductProperties.search.encryptedProperties,
      shoppedProductProperties.room.encryptedProperties,
      shoppedProductProperties.roomRate.encryptedProperties,
      shoppedProductProperties.hotel.encryptedProperties,
      ...extraEncryptedProperties,
    ].filter((x): x is NonNullable<typeof x> => x != null),
  };
};

const getShoppedProductProperties = ({
  trackingAttributes,
}: HotelsCheckoutProviders): HotelShoppedProductTrackingProperties => {
  const attributes = trackingAttributes.getAllAttributes();
  if (attributes.hotelCheckoutShoppedProduct == null) {
    logNotFoundTrackingProperties(
      "getShoppedProductProperties",
      "hotels checkout"
    );
    return fallbackHotelShoppedProductTrackingProperties;
  }

  return attributes.hotelCheckoutShoppedProduct;
};

const getQuoteProperties = ({
  trackingAttributes,
}: HotelsCheckoutProviders) => {
  const attributes = trackingAttributes.getAllAttributes();
  if (attributes.checkoutQuotedProduct == null) {
    logNotFoundTrackingProperties("getQuoteProperties", "checkout");
    return fallbackCheckoutQuotedProductTrackingProperties;
  }

  return attributes.checkoutQuotedProduct;
};

const getQuoteEncryptedProperties = ({
  trackingAttributes,
}: HotelsCheckoutProviders) => {
  const attributes = trackingAttributes.getAllAttributes();
  if (attributes.quoteEncryptedTrackingProperties == null) {
    logNotFoundTrackingProperties(
      "quoteEncryptedTrackingProperties",
      "checkout"
    );
    return [];
  }
  return attributes.quoteEncryptedTrackingProperties;
};

const getPaymentMethodProperties = ({
  trackingAttributes,
}: HotelsCheckoutProviders) => {
  const attributes = trackingAttributes.getAllAttributes();
  if (attributes.checkoutPaymentMethod == null) {
    logNotFoundTrackingProperties("getPaymentMethodProperties", "checkout");
    return fallbackCheckoutPaymentMethodTrackingProperties;
  }

  return attributes.checkoutPaymentMethod;
};

const getPurchaseCompletedTrackingProperties = ({
  trackingAttributes,
}: HotelsCheckoutProviders) => {
  const attributes = trackingAttributes.getAllAttributes();
  if (attributes.checkoutPurchaseCompletedTrackingProperties == null) {
    logNotFoundTrackingProperties(
      "getPurchaseCompletedTrackingProperties",
      "checkout"
    );
    return fallbackPurchaseCompletedTrackingProperties;
  }

  return attributes.checkoutPurchaseCompletedTrackingProperties;
};

const getCartFulfillFailureReason = ({
  trackingAttributes,
}: HotelsCheckoutProviders) => {
  const attributes = trackingAttributes.getAllAttributes();
  if (attributes.checkoutCartFulfillFailureReason == null) {
    logNotFoundTrackingProperties("getCartFulfillFailureReason", "checkout");
    return {
      failure_reason: "",
    };
  }

  return {
    failure_reason: attributes.checkoutCartFulfillFailureReason,
  };
};
